import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../../contexts/ThemeContext';

interface HeatmapGridProps {
  data?: { date: string; value: number }[];
  series?: { label: string; color: string; data: { date: string; value: number }[] }[];
  days?: number;
  onCellClick?: (date: string, value: number, seriesLabel?: string) => void;
}

const HeatmapGrid: React.FC<HeatmapGridProps> = ({ data, series, days = 90, onCellClick }) => {
  const { darkMode } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    d3.select(el).selectAll('*').remove();

    const cellSize = 12;
    const cellGap = 2;
    const labelWidth = series ? 90 : 0;
    const today = new Date();
    const startDate = d3.timeDay.offset(today, -days);
    const allDates = d3.timeDays(startDate, today);
    const emptyColor = darkMode ? '#1f2937' : '#f3f4f6';

    if (series && series.length > 0) {
      const height = series.length * (cellSize + cellGap + 4) + 24;
      const width = labelWidth + allDates.length * (cellSize + cellGap) + 20;
      const svg = d3.select(el).append('svg')
        .attr('width', Math.max(width, el.clientWidth))
        .attr('height', height);

      series.forEach((s, rowIdx) => {
        const y = rowIdx * (cellSize + cellGap + 4) + 4;
        const valueMap = new Map(s.data.map(d => [d.date, d.value]));
        const maxVal = d3.max(s.data, d => d.value) || 1;
        const colorScale = d3.scaleSequential(
          d3.interpolateRgb(emptyColor, s.color)
        ).domain([0, maxVal]);

        svg.append('text')
          .attr('x', labelWidth - 6)
          .attr('y', y + cellSize - 1)
          .attr('text-anchor', 'end')
          .attr('font-size', 10)
          .attr('fill', s.color)
          .attr('font-weight', 500)
          .text(s.label);

        allDates.forEach((date, i) => {
          const dateStr = d3.timeFormat('%Y-%m-%d')(date);
          const val = valueMap.get(dateStr) || 0;
          svg.append('rect')
            .attr('x', labelWidth + i * (cellSize + cellGap))
            .attr('y', y)
            .attr('width', cellSize)
            .attr('height', cellSize)
            .attr('rx', 2)
            .attr('fill', val > 0 ? colorScale(val) : emptyColor)
            .attr('cursor', onCellClick ? 'pointer' : 'default')
            .on('click', () => onCellClick?.(dateStr, val, s.label))
            .append('title')
            .text(`${s.label}: ${dateStr} \u2014 ${val} events`);
        });
      });

      // Month labels
      const months = d3.timeMonths(startDate, today);
      months.forEach(month => {
        const idx = d3.timeDay.count(startDate, month);
        if (idx >= 0 && idx < allDates.length) {
          svg.append('text')
            .attr('x', labelWidth + idx * (cellSize + cellGap))
            .attr('y', height - 4)
            .attr('font-size', 9)
            .attr('fill', darkMode ? '#6b7280' : '#9ca3af')
            .text(d3.timeFormat('%b')(month));
        }
      });

    } else if (data) {
      const weeks = Math.ceil(days / 7);
      const height = 7 * (cellSize + cellGap) + 24;
      const width = weeks * (cellSize + cellGap) + 40;
      const svg = d3.select(el).append('svg')
        .attr('width', Math.max(width, el.clientWidth))
        .attr('height', height);

      const maxVal = d3.max(data, d => d.value) || 1;
      const colorScale = d3.scaleSequential(
        d3.interpolateRgb(emptyColor, '#22c55e')
      ).domain([0, maxVal]);

      const valueMap = new Map(data.map(d => [d.date, d.value]));

      allDates.forEach((date, i) => {
        const dateStr = d3.timeFormat('%Y-%m-%d')(date);
        const val = valueMap.get(dateStr) || 0;
        const week = Math.floor(i / 7);
        const dayOfWeek = i % 7;

        svg.append('rect')
          .attr('x', week * (cellSize + cellGap))
          .attr('y', dayOfWeek * (cellSize + cellGap))
          .attr('width', cellSize)
          .attr('height', cellSize)
          .attr('rx', 2)
          .attr('fill', val > 0 ? colorScale(val) : emptyColor)
          .attr('cursor', onCellClick ? 'pointer' : 'default')
          .on('click', () => onCellClick?.(dateStr, val))
          .append('title')
          .text(`${dateStr}: ${val} events`);
      });

      // Month labels
      const months = d3.timeMonths(startDate, today);
      months.forEach(month => {
        const idx = d3.timeDay.count(startDate, month);
        const week = Math.floor(idx / 7);
        svg.append('text')
          .attr('x', week * (cellSize + cellGap))
          .attr('y', height - 4)
          .attr('font-size', 9)
          .attr('fill', darkMode ? '#6b7280' : '#9ca3af')
          .text(d3.timeFormat('%b')(month));
      });
    }
  }, [data, series, days, darkMode, onCellClick]);

  return <div ref={containerRef} className="overflow-x-auto" />;
};

export default HeatmapGrid;
